import { useCallback, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import ModalsComponent from '../stories/atoms/modals'
import { modalSizeOptions } from '../stories/atoms/modals/constants'
import TextInputComponent from '../stories/molecules/text-input'
import {
  buttonVariantOptions,
  buttonSizeOptions
} from '../stories/atoms/button/constants'

import {
  FormContainer,
  ModalBody,
  ModalContentContainer,
  TotalItemsToMove,
  TransparentModalContentContainer
} from '../../containers/project-menu/styled-component'

import StaticBannerComponent from '../stories/molecules/static-banner'
import { staticBannerVariantOptions } from '../stories/molecules/static-banner/constants'
import RecursiveFolderList from '../recursive-folder-list'
import { SpinnerGrey } from '../stories/atoms/icons/special'
import { iconSizeOptions } from '../stories/atoms/icons/icons-sizes'
import PropTypes from 'prop-types'
import { recursiveListTypeOptions } from '../recursive-folder-list/constants'

const MoveItemsModal = ({
  handleClose = () => {},
  handleMove = () => {},
  isLoading = false,
  onMoveLoading = false,
  onMoveError = null,
  destinationFolderHint = null,
  modalTitle = '',
  oldFolderName = '',
  entityName = '',
  isMultipleItems = false,
  totalSelectedItems = 0,
  foldersListType = recursiveListTypeOptions.PROJECT,
  planRootFolderId = null,
  destinationFolderId = null,
  // folders being moved: they can't be their own destination
  excludedFolderIds = []
}) => {
  const { formatMessage } = useIntl()
  const [parentFolderId, setParentFolderId] = useState(null)

  const handleSelectFolderId = (folder) => {
    if (folder && folder.id) {
      return setParentFolderId(folder.id)
    }

    return setParentFolderId(null)
  }

  // handlers
  const handleMoveItems = useCallback(() => {
    handleMove({
      parentFolderId
    })
  }, [handleMove, parentFolderId])

  const modalButtons = [
    {
      title: formatMessage({ id: 'cancel' }),
      variant: buttonVariantOptions['TERTIARY-HOLLOW'],
      size: buttonSizeOptions.MEDIUM,
      onClick: handleClose
    },
    {
      title: formatMessage({ id: 'move' }),
      variant: buttonVariantOptions.PRIMARY,
      size: buttonSizeOptions.MEDIUM,
      onClick: handleMoveItems,
      disabled: isLoading,
      isLoading: onMoveLoading
    }
  ]

  return (
    <ModalsComponent
      isOpen={true}
      title={modalTitle}
      size={modalSizeOptions.SMALL}
      rightButtons={modalButtons}
      onClose={handleClose}
      showCloseButton={false}
      autoHeight
    >
      <ModalBody>
        {isLoading ? (
          <SpinnerGrey size={iconSizeOptions['ICON-L']} />
        ) : (
          <>
            {onMoveError && (
              <StaticBannerComponent
                variant={staticBannerVariantOptions.ERROR}
                message={onMoveError}
              />
            )}
            <TransparentModalContentContainer>
              {isMultipleItems ? (
                <TotalItemsToMove>
                  <FormattedMessage
                    id="moveItems_move_multiple_items"
                    values={{ totalSelectedItems }}
                  />
                </TotalItemsToMove>
              ) : (
                <FormContainer>
                  <TextInputComponent
                    defaultValue={entityName}
                    label={formatMessage({ id: 'title' })}
                    showCounter={false}
                    isTextarea
                    readOnly={true}
                  />
                  <TextInputComponent
                    defaultValue={oldFolderName}
                    label={formatMessage({ id: 'current_folder' })}
                    showCounter={false}
                    isTextarea
                    readOnly={true}
                  />
                </FormContainer>
              )}
            </TransparentModalContentContainer>
            <ModalContentContainer>
              <FormContainer>
                <RecursiveFolderList
                  onSelectItem={handleSelectFolderId}
                  textInputHint={destinationFolderHint}
                  listType={foldersListType}
                  planRootFolderId={planRootFolderId}
                  defaultFolderId={destinationFolderId}
                  excludedFolderIds={excludedFolderIds}
                />
              </FormContainer>
            </ModalContentContainer>
          </>
        )}
      </ModalBody>
    </ModalsComponent>
  )
}

MoveItemsModal.propTypes = {
  handleClose: PropTypes.func,
  handleMove: PropTypes.func,
  isLoading: PropTypes.bool,
  onMoveLoading: PropTypes.bool,
  onMoveError: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  destinationFolderHint: PropTypes.string,
  modalTitle: PropTypes.string,
  oldFolderName: PropTypes.string,
  entityName: PropTypes.string,
  isMultipleItems: PropTypes.bool,
  totalSelectedItems: PropTypes.number,
  foldersListType: PropTypes.oneOf(Object.values(recursiveListTypeOptions)),
  planRootFolderId: PropTypes.string,
  excludedFolderIds: PropTypes.arrayOf(PropTypes.string)
}

export default MoveItemsModal
